import React, { useState } from "react";
import { 
  FileText, Book, Code, Database, Rocket, Users, Shield,
  ChevronRight, ChevronDown, Copy, Check, ExternalLink, TrendingUp, Server
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const DOCS = [
  {
    id: "architecture",
    title: "Architecture Overview",
    icon: Code,
    description: "Tech stack, system design, directory structure",
    audience: "Investors, Developers",
  },
  {
    id: "api",
    title: "API Reference",
    icon: FileText,
    description: "All API endpoints with request/response examples",
    audience: "Developers",
  },
  {
    id: "database",
    title: "Database Schema",
    icon: Database,
    description: "MongoDB collections & data models",
    audience: "Developers",
  },
  {
    id: "features",
    title: "Features Guide",
    icon: Book,
    description: "Every feature explained in detail",
    audience: "Everyone",
  },
  {
    id: "financial",
    title: "Financial Forecast",
    icon: TrendingUp,
    description: "3-year revenue projections & business model",
    audience: "Investors, Founders",
  },
  {
    id: "capacity",
    title: "Capacity Planning",
    icon: Server,
    description: "Current setup limits & scaling guide",
    audience: "Founders, Developers",
  },
  {
    id: "deployment",
    title: "Deployment Guide",
    icon: Rocket,
    description: "How to deploy web & mobile apps",
    audience: "Developers",
  },
  {
    id: "handoff",
    title: "Developer Handoff",
    icon: Users,
    description: "Quick start guide for new developers",
    audience: "New Developers",
  },
  {
    id: "admin",
    title: "Admin Guide",
    icon: Shield,
    description: "How to use the admin dashboard",
    audience: "Admin Users",
  },
];

const AdminDocs = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadDocument = async (docId) => {
    setLoading(true);
    setSelectedDoc(docId);
    
    try {
      const BACKEND_URL = "https://api.recoverymatch.com";
      const response = await fetch(`${BACKEND_URL}/api/admin/docs/${docId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("recoverymatch_admin_token") || localStorage.getItem("token")}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocContent(data.content);
      } else {
        toast.error("Failed to load document");
        setDocContent("Error loading document");
      }
    } catch (error) {
      toast.error("Failed to load document");
      setDocContent("Error loading document");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(docContent);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadDoc = () => {
    const doc = DOCS.find(d => d.id === selectedDoc);
    const filename = `RecoveryMatch_${doc?.title.replace(/\s+/g, '_')}.md`;
    const blob = new Blob([docContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  return (
    <AdminLayout>
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Documentation</h1>
        <p className="text-gray-600">
          Technical documentation for RecoveryMatch. These files are protected and only visible to admins.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Document List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-900">Available Documents</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {DOCS.map((doc) => {
                const Icon = doc.icon;
                const isSelected = selectedDoc === doc.id;
                
                return (
                  <button
                    key={doc.id}
                    onClick={() => loadDocument(doc.id)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition flex items-start gap-3 ${
                      isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {doc.title}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{doc.description}</p>
                      <span className="text-xs text-gray-400 mt-1 inline-block">
                        {doc.audience}
                      </span>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-gray-400 flex-shrink-0 ${isSelected ? 'text-blue-500' : ''}`} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Document Viewer */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full">
            {!selectedDoc ? (
              <div className="flex flex-col items-center justify-center h-96 text-gray-400">
                <FileText className="w-16 h-16 mb-4" />
                <p className="text-lg">Select a document to view</p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* Document Header */}
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-900">
                      {DOCS.find(d => d.id === selectedDoc)?.title}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {DOCS.find(d => d.id === selectedDoc)?.description}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="gap-2"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadDoc}
                      className="gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Download
                    </Button>
                  </div>
                </div>
                
                {/* Document Content */}
                <div className="p-6 overflow-auto max-h-[600px]">
                  <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 leading-relaxed">
                    {docContent}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-amber-900">Protected Documentation</h3>
          <p className="text-sm text-amber-700 mt-1">
            These documents contain sensitive technical information about RecoveryMatch.
            Only share with trusted team members and developers. The documentation is also
            available in your GitHub repository under <code className="bg-amber-100 px-1 rounded">/docs/</code>.
          </p>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminDocs;
